/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reatomSet, withAssign } from '@reatom/framework'
import { match } from 'ts-pattern'

import { Array, AsyncStateN } from '@blade/std-lib'
import {
  reatomWallet,
  walletsModelAtom,
  walletsStateAtom,
} from '@root/entities/wallet'
import { reatomDataTable } from '@root/shared/ui-kit'

import { walletManagerTableColumns } from '../ui/wallets-table-columns'

import type { WalletModel } from '@root/entities/wallet'
import type { TableData } from '@root/shared/ui-kit'

export type WalletsTableColumnId =
  | 'check'
  | 'name'
  | 'address'
  | 'transactions'
  | 'balance'
  | 'assets'
  | 'chart'
  | 'edit'

export const reatomUserWalletsTable = (args?: {
  withSelection?: boolean
  withTransactions?: boolean
}) => {
  const { withSelection = true, withTransactions = true } = args ?? {}

  const stateAtom = atom(
    (ctx): AsyncStateN.AsyncStateN<unknown, TableData<WalletModel>> => {
      const state = ctx.spy(walletsStateAtom)
      if (!AsyncStateN.isSuccess(state)) {
        return state
      }

      const sorting = ctx.spy(model.sorting)
      const wallets = state.value

      const cmp = match(sorting)
        .with({ sortColumn: 'balance' }, ({ sortDirection }) =>
          sortDirection === 'asc'
            ? reatomWallet.cmpBalanceAsc(ctx)
            : reatomWallet.cmpBalanceDesc(ctx),
        )
        .with({ sortColumn: 'assets' }, ({ sortDirection }) =>
          sortDirection === 'asc'
            ? reatomWallet.cmpAssetsCountAsc(ctx)
            : reatomWallet.cmpAssetsCountDesc(ctx),
        )
        .otherwise(() => reatomWallet.cmpByOrderAsc(ctx))

      const walletsSorted = Array.sort(cmp)(wallets)

      return AsyncStateN.success({ data: walletsSorted })
    },
  )

  const model = reatomDataTable({
    name: 'wallet-manager-table',
    columns: walletManagerTableColumns.filter((it) => {
      if (it.id === 'check' && withSelection === false) {
        return false
      }
      // eslint-disable-next-line sonarjs/prefer-single-boolean-return
      if (it.id === 'transactions' && withTransactions === false) {
        return false
      }

      return true
    }),
    getId: ({ address }) => address,
    stateAtom,
  })

  const expandedWalletsAtom = reatomSet<WalletModel>()
  const toggleExpandedWalletAction = action((ctx, wallet: WalletModel) => {
    expandedWalletsAtom.toggle(ctx, wallet)
  })
  const clearExpandedWalletsAction = action((ctx) => {
    expandedWalletsAtom.clear(ctx)
  })

  const retryAction = action((ctx) => {
    const model = ctx.get(walletsModelAtom)
    model.load(ctx)
  })

  return model.pipe(
    withAssign(() => ({
      expandedWallets: expandedWalletsAtom,
      toggleExpandedWallet: toggleExpandedWalletAction,
      clearExpandedWallets: clearExpandedWalletsAction,
      retry: retryAction,
    })),
  )
}
